<template>
  <div v-if="inline" class="tp-inline">
    <div class="tp-wheels" aria-label="time">
      <div class="tp-highlight" aria-hidden="true"></div>
      <div
        ref="hourCol"
        class="tp-col"
        @scroll="onColScroll('hour')"
      >
        <div class="tp-pad" aria-hidden="true"></div>
        <div
          v-for="h in hours"
          :key="'h' + h"
          class="tp-item"
          :class="{ active: h === hour }"
        >
          {{ pad(h) }}
        </div>
        <div class="tp-pad" aria-hidden="true"></div>
      </div>
      <div class="tp-sep" aria-hidden="true">:</div>
      <div
        ref="minCol"
        class="tp-col"
        @scroll="onColScroll('min')"
      >
        <div class="tp-pad" aria-hidden="true"></div>
        <div
          v-for="m in minutes"
          :key="'m' + m"
          class="tp-item"
          :class="{ active: m === minute }"
        >
          {{ pad(m) }}
        </div>
        <div class="tp-pad" aria-hidden="true"></div>
      </div>
    </div>
    <div class="tp-actions">
      <button type="button" class="tp-btn tp-btn--ghost" @click="clearTime">
        {{ $t("ui.clear") }}
      </button>
      <button type="button" class="tp-btn tp-btn--primary" @click="confirmTime">
        {{ $t("todoDetails.done") }}
      </button>
    </div>
  </div>
  <div v-else class="dropdown time-picker-root">
    <div
      class="header-menu-icons time-picker-toggle"
      type="button"
      data-bs-toggle="dropdown"
      data-bs-auto-close="outside"
      data-bs-popper-config='{"strategy":"fixed","placement":"bottom-end"}'
      :title="$t('todoDetails.time')"
    >
      <i :class="{ 'bi-alarm': !selectedTime, 'bi-alarm-fill': selectedTime }"></i>
    </div>

    <ul class="dropdown-menu color-picker-dropdown">
      <div class="d-flex align-items-center mx-3">
        <input type="time" v-model="selectedTime" @change="selectTime(selectedTime)" @blur="selectTime(selectedTime)" />
        <i class="header-menu-icons bi-trash" type="button" @click="clearTime"></i>
      </div>
    </ul>
  </div>
</template>

<script>
const ITEM_H = 40;

function range(n) {
  return Array.from({ length: n }, (_, i) => i);
}

export default {
  name: "timePicker",
  emits: ["timeSelected"],
  data() {
    return {
      selectedTime: this.time || "",
      hour: 9,
      minute: 0,
      hours: range(24),
      minutes: range(60),
      scrollTimers: { hour: null, min: null },
      syncingScroll: false,
    };
  },
  props: {
    time: { required: true, type: [String, null] },
    inline: { type: Boolean, default: false },
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, "0");
    },
    parseTime(val) {
      if (!val || typeof val !== "string" || !val.includes(":")) {
        const now = new Date();
        return { hour: now.getHours(), minute: now.getMinutes() };
      }
      const [h, m] = val.split(":").map((x) => parseInt(x, 10));
      return {
        hour: Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 9,
        minute: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0,
      };
    },
    applyFromProp(val) {
      const { hour, minute } = this.parseTime(val);
      this.hour = hour;
      this.minute = minute;
      this.selectedTime = val || "";
      this.$nextTick(() => this.scrollToCurrent(false));
    },
    scrollToCurrent(smooth) {
      this.syncingScroll = true;
      const hourEl = this.$refs.hourCol;
      const minEl = this.$refs.minCol;
      const behavior = smooth ? "smooth" : "auto";
      if (hourEl) {
        hourEl.scrollTo({ top: this.hour * ITEM_H, behavior });
      }
      if (minEl) {
        minEl.scrollTo({ top: this.minute * ITEM_H, behavior });
      }
      requestAnimationFrame(() => {
        this.syncingScroll = false;
      });
    },
    onColScroll(which) {
      if (this.syncingScroll) return;
      clearTimeout(this.scrollTimers[which]);
      this.scrollTimers[which] = setTimeout(() => {
        this.snapCol(which);
      }, 80);
    },
    snapCol(which) {
      const el = which === "hour" ? this.$refs.hourCol : this.$refs.minCol;
      if (!el) return;
      const idx = Math.round(el.scrollTop / ITEM_H);
      const max = which === "hour" ? 23 : 59;
      const clamped = Math.min(max, Math.max(0, idx));
      this.syncingScroll = true;
      el.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
      if (which === "hour") this.hour = clamped;
      else this.minute = clamped;
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.syncingScroll = false;
        }, 120);
      });
    },
    confirmTime() {
      const time = `${this.pad(this.hour)}:${this.pad(this.minute)}`;
      this.selectedTime = time;
      this.selectTime(time);
    },
    selectTime(time) {
      this.$emit("timeSelected", time || null);
    },
    clearTime() {
      this.selectedTime = "";
      this.selectTime(null);
    },
  },
  watch: {
    time: {
      immediate: true,
      handler(newVal) {
        if (this.inline) this.applyFromProp(newVal);
        else this.selectedTime = newVal || "";
      },
    },
  },
  mounted() {
    if (this.inline) {
      this.$nextTick(() => this.scrollToCurrent(false));
    }
  },
  beforeUnmount() {
    clearTimeout(this.scrollTimers.hour);
    clearTimeout(this.scrollTimers.min);
  },
};
</script>

<style scoped lang="scss">
@import "/src/assets/style/globalVars.scss";

.header-menu-icons {
  margin-left: 6px;
  @include btn-icon;
}

.bi-trash {
  margin: 0px;
}

/* —— 行内滚轮 —— */
.tp-inline {
  width: 100%;
}

.tp-wheels {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 4px;
  height: 200px; /* 5 × 40 */
  margin: 4px 0 10px;
  user-select: none;
  -webkit-user-select: none;
}

.tp-highlight {
  position: absolute;
  left: 8px;
  right: 8px;
  top: 50%;
  height: 40px;
  margin-top: -20px;
  border-radius: 10px;
  background: rgba(118, 118, 128, 0.12);
  pointer-events: none;
  z-index: 0;
}

.dark-theme .tp-highlight {
  background: rgba(118, 118, 128, 0.28);
}

.tp-col {
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  max-width: 120px;
  height: 100%;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  text-align: center;
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    #000 18%,
    #000 82%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    #000 18%,
    #000 82%,
    transparent 100%
  );

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.tp-pad {
  height: 80px; /* 2 × 40 */
}

.tp-item {
  height: 40px;
  line-height: 40px;
  scroll-snap-align: center;
  font-size: 22px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: rgba(60, 60, 67, 0.35);
  transition: color 0.15s, font-size 0.15s, font-weight 0.15s;

  &.active {
    color: #000;
    font-size: 26px;
    font-weight: 650;
  }
}

.dark-theme .tp-item {
  color: rgba(235, 235, 245, 0.3);

  &.active {
    color: #fff;
  }
}

.tp-sep {
  position: relative;
  z-index: 1;
  align-self: center;
  width: 12px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  color: #8e8e93;
  pointer-events: none;
}

.tp-actions {
  display: flex;
  gap: 8px;
}

.tp-btn {
  flex: 1;
  border: none;
  border-radius: 10px;
  padding: 11px 12px;
  font-size: 15px;
  font-weight: 560;
  -webkit-tap-highlight-color: transparent;
}

.tp-btn--ghost {
  background: rgba(118, 118, 128, 0.12);
  color: #ff3b30;
}

.tp-btn--primary {
  background: #007aff;
  color: #fff;
}

.dark-theme .tp-btn--ghost {
  background: rgba(118, 118, 128, 0.24);
}

/* —— 桌面下拉（保持原样） —— */
input[type="time"] {
  background-color: transparent;
  border: none;
  font-size: 16px;
  width: 130px;
  outline: unset;
  height: 40px;
  flex: 1;
}

input[type="time"]::-webkit-datetime-edit-text {
  padding: 19px 2px;
}

input[type="time"]::-webkit-datetime-edit-fields-wrapper {
  padding: 8px 2px 8px 2px;
  border: none;
}

input[type="time"]::-webkit-datetime-edit-hour-field,
input[type="time"]::-webkit-datetime-edit-minute-field,
input[type="time"]::-webkit-datetime-edit-ampm-field {
  background-color: transparent;
  border: 2px solid transparent;
  border-radius: 5px;
  padding: 5px;
  min-width: 80px;
  width: 80px;
  color: #494949;

  .dark-theme & {
    color: #bfbfbf;
  }
}

.time-picker-root input[type="time"]::-webkit-calendar-picker-indicator {
  background: none;
  display: none;
}

input[type="time"]::-webkit-datetime-edit-hour-field:hover,
input[type="time"]::-webkit-datetime-edit-minute-field:hover,
input[type="time"]::-webkit-datetime-edit-ampm-field:hover {
  color: black;
  background-color: #f4f4f4;

  .dark-theme & {
    color: white;
    background-color: #303940;
  }
}

input[type="time"]::-webkit-datetime-edit-hour-field:focus,
input[type="time"]::-webkit-datetime-edit-minute-field:focus,
input[type="time"]::-webkit-datetime-edit-ampm-field:focus {
  border: 2px solid black;
  color: black;
  background-color: transparent;

  .dark-theme & {
    border: 2px solid white;
    color: white;
  }
}
</style>
